<div class="sk-detail">
  <div class="sk-detail__head">
    <div class="sk-detail__titles">
      <div class="modal-sub">{{ skill.kategorie }}</div>
      <h2 class="sk-detail__title">{{ skill.faehigkeit }}</h2>
      <div class="sk-detail__en">
        @if (hasEnglishName()) {
          <span class="sk-lang">EN</span>{{ skill.faehigkeitEn }}
        } @else {
          <span class="sk-noen sk-detail__noen">{{
            'skillManagement.detail.enMissing' | translate
          }}</span>
        }
      </div>
    </div>
    <div class="sk-detail__actions">
      <button
        type="button"
        class="of-del"
        (click)="requestDelete()"
        [title]="'skillManagement.detail.delete' | translate"
        [attr.aria-label]="'skillManagement.detail.delete' | translate"
      >
        <app-skill-icon name="trash" [size]="17" />
      </button>
      <button
        type="button"
        class="btn-primary"
        [disabled]="!canSave"
        (click)="save()"
        [title]="
          englishMissing || germanMissing
            ? ('skillManagement.detail.saveBlocked' | translate)
            : ''
        "
      >
        {{ 'general.save' | translate }}
      </button>
    </div>
  </div>

  <div class="sk-detail__grid">
    <div class="sk-lang-pair">
      <label class="bf-field">
        <span class="bf-label"
          ><span class="sk-lang">DE</span
          >{{ 'skillManagement.detail.nameDe' | translate }}</span
        >
        <input
          class="bf-input"
          lang="de"
          [class.bf-input--invalid]="germanMissing"
          [attr.aria-invalid]="germanMissing"
          [maxlength]="limits.SHORT_LABEL"
          [(ngModel)]="form.faehigkeit"
        />
        @if (germanMissing) {
          <span class="sk-hint sk-hint--warn">{{
            'skillManagement.detail.nameRequired' | translate
          }}</span>
        }
      </label>
      <label class="bf-field">
        <span class="bf-label"
          ><span class="sk-lang">EN</span
          >{{ 'skillManagement.detail.nameEn' | translate }}</span
        >
        <input
          class="bf-input"
          lang="en"
          [class.bf-input--invalid]="englishMissing"
          [attr.aria-invalid]="englishMissing"
          [maxlength]="limits.SHORT_LABEL"
          [placeholder]="'skillManagement.detail.nameEnPlaceholder' | translate"
          [(ngModel)]="form.faehigkeitEn"
        />
        @if (englishMissing) {
          <span class="sk-hint sk-hint--warn">{{
            'skillManagement.detail.nameEnRequired' | translate
          }}</span>
        } @else if (sameNameInBothLanguages) {
          <span class="sk-hint">{{
            'skillManagement.detail.sameName' | translate
          }}</span>
        }
      </label>
      <div class="bf-field">
        <span class="bf-label" aria-hidden="true">&nbsp;</span>
        <button
          type="button"
          class="sk-copy"
          (click)="copyGermanToEnglish()"
          [title]="'skillManagement.detail.copyDeToEnTitle' | translate"
        >
          {{ 'skillManagement.detail.copyDeToEn' | translate }}
        </button>
      </div>
    </div>

    <div class="bf-field">
      <span class="bf-label">{{
        'skillManagement.detail.category' | translate
      }}</span>
      <app-ci-select
        class="sk-category"
        variant="outline"
        [(ngModel)]="form.kategorie"
        [items]="categoryOptions"
        [ariaLabel]="'skillManagement.detail.category' | translate"
        [placeholder]="'skillCategorySelect.placeholder' | translate"
        [searchPlaceholder]="'skillCategorySelect.search' | translate"
        [searchClearLabel]="'ciSelect.clearSearch' | translate"
        [emptyLabel]="'skillCategorySelect.empty' | translate"
        [hitTemplate]="'ciSelect.hits' | translate"
      ></app-ci-select>
      @if (categoryMoved) {
        <span class="sk-hint">{{
          'skillManagement.detail.categoryMove'
            | translate: { category: form.kategorie }
        }}</span>
      }
    </div>

    <div class="bf-field">
      <label class="bf-label" for="sk-wiki-input">{{
        'skillManagement.detail.wiki' | translate
      }}</label>
      <div class="sk-wiki">
        <input
          id="sk-wiki-input"
          class="bf-input"
          [maxlength]="limits.URL"
          [(ngModel)]="form.wikipage"
        />
        @if (wikiUrl) {
          <a
            class="sk-wiki__link"
            [href]="wikiUrl"
            target="_blank"
            rel="noreferrer"
            [title]="'skillManagement.detail.wikiOpen' | translate"
            [attr.aria-label]="'skillManagement.detail.wikiOpen' | translate"
          >
            <app-skill-icon name="external" [size]="15" />
          </a>
        } @else {
          <span
            class="sk-wiki__link sk-wiki__link--disabled"
            role="link"
            aria-disabled="true"
            [title]="'skillManagement.detail.wikiOpen' | translate"
            [attr.aria-label]="'skillManagement.detail.wikiOpen' | translate"
          >
            <app-skill-icon name="external" [size]="15" />
          </span>
        }
      </div>
    </div>
  </div>

  <div class="sk-flags">
    @for (option of flagOptions; track option.flag) {
      <button
        type="button"
        class="fchip"
        [class.fchip--on]="form.flags[option.flag]"
        [attr.aria-pressed]="form.flags[option.flag]"
        (click)="toggleFlag(option.flag)"
      >
        {{ option.label | translate }}
      </button>
    }
  </div>

  <app-skill-usage [usage]="usage" [state]="usageState" />

  <section class="sk-sim" #similarSection>
    <h3 class="sk-h3" #similarHeading tabindex="-1">
      {{ 'skillManagement.similar.title' | translate }}
      <span class="sk-h3__note">{{
        'skillManagement.similar.subtitle' | translate
      }}</span>
    </h3>
    @if (similar.length === 0) {
      <div class="sk-empty sk-empty--left">
        {{ 'skillManagement.similar.none' | translate }}
      </div>
    } @else {
      <div
        class="sk-sim__list"
        role="radiogroup"
        [attr.aria-label]="'skillManagement.similar.title' | translate"
      >
        @for (entry of similar; track entry.skill.id) {
          <label
            class="sk-sim__item"
            [class.sk-sim__item--on]="entry.skill.id === mergeSourceId"
          >
            <input
              type="radio"
              name="sk-merge"
              [value]="entry.skill.id"
              [(ngModel)]="mergeSourceId"
            />
            <span class="sk-sim__name">{{ entry.skill.faehigkeit }}</span>
            <span class="sk-hit-cat">{{ entry.skill.kategorie }}</span>
            <span class="sk-muted sk-sim__count">
              {{
                entry.consultants === null
                  ? '–'
                  : ('skillManagement.similar.consultants'
                    | translate: { count: entry.consultants })
              }}
            </span>
          </label>
        }
      </div>
      <div class="sk-sim__foot">
        <span class="sk-muted">
          @if (mergeSource; as source) {
            {{
              'skillManagement.similar.willMerge'
                | translate
                  : {
                      source: source.faehigkeit + ' · ' + source.kategorie,
                      target: skill.faehigkeit + ' · ' + skill.kategorie,
                    }
            }}
          } @else {
            {{ 'skillManagement.similar.pick' | translate }}
          }
        </span>
        <button
          type="button"
          class="tpl-btn"
          [disabled]="!mergeSource || merging"
          (click)="merge()"
        >
          <app-skill-icon name="merge" [size]="17" />{{
            'skillManagement.similar.merge' | translate
          }}
        </button>
      </div>
    }
  </section>
</div>
